import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { InfoBook } from '../../../../../model/book/Book';
import { IOffer } from '../../../../../model/offer/IOffer';
import { IParams } from '../../../../../model/shared/IParams';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { isDateInPast } from '../../../../../shared/functions/Functions';
import { IAllStates } from '../../../../../store/rootReducer';
import { gtmService } from '../../../../../utils/gtm.service';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../../utils/toasters';
import { IDeclineOffer } from '../../../../influencer/offer/dto/IDeclineOffer';
import { BookingService } from '../service';
import { BookingCardItem } from './BookingCardItem';
import { BookingFilter } from './BookingFilter';
import { BookingListItem } from './BookingListItem';
import BookingOverviewCard from './BookingOverviewCard';
import { IOfferFilter } from './dto/IOfferFilter';
import { IOfferOverviewCart } from './dto/IOfferOverviewCart';
import ContractsOverviewCard from '../child-offer/ContractsOverviewCard';
import { IContract } from '../../../../../model/shared/IContract';
import { scrollToTop } from '../../../../../utils/helpers/ScrollToTop';
import { BookingCardSkeleton } from './BookingCardSkeleton';
import { StoreKeeper } from '../../../../../store/store';
import { loadingSlice } from '../../../../../store/general/loadingSlice';
import { ContractsOverviewCardSkeleton } from '../child-offer/ContractsOverviewCardSkeleton';
import { BookingOverviewCardSkeleton } from './BookingOverviewCardSkeleton';

interface ILocationState {
    isSentActive: boolean;
    isFromNotification?: boolean;
}

const SKELETON_COUNT = 8;

export function OfferBook() {
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();
    const location = useLocation();
    const { campaign, isChildCampaign } = useSelector((states: IAllStates) => states.selectedCampaign);
    const { hasUnreadComments } = useSelector((state: IAllStates) => state.offerBrand);
    const { isClient } = useSelector((states: IAllStates) => states.auth.user);
    const [isOverviewLoading, setIsOverviewLoading] = useState(true);
    const [isContractsInitialLoading, setIsContractsInitialLoading] = useState(true);
    const [isContractsLoading, setIsContractsLoading] = useState(false);
    const lastContractsKeyRef = useRef<string>();
    const getActiveOfferFilter = (info: InfoBook | undefined) => {
        if (isClient) {
            return 'proposed';
        }
        if (isDateInPast(campaign?.publishFrom ?? '')) {
            return 'booked';
        }
        if (!info?.bookingCount) {
            return 'received';
        }
        return 'booked';
    };

    const [isOffersLoading, setIsOffersLoading] = useState(true);
    const lastQueryKeyRef = useRef<string>();
    const [infoCampaign, setInfoCampaign] = useState<InfoBook>();
    const [filterParams, setFilterParams] = useState<IOfferFilter>({
        page: 1,
        perPage: 10,
        sortCriteria: '',
        sortType: 'DESC',
        platformId: [],
        isBestPick: false,
        reachFrom: '',
        reachTo: '',
        priceRangeFrom: '',
        priceRangeTo: '',
        influencerLocation: undefined,
        audienceLocation: undefined,
        offerFilters: (location?.state as ILocationState)?.isSentActive ? 'sent' : getActiveOfferFilter(infoCampaign),
        status: undefined,
        displayName: '',
    });

    useEffect(() => {
        StoreKeeper.store.dispatch(loadingSlice.actions.setIsLoading(false));
    });

    useEffect(() => {
        if (isClient) {
            setFilterParams(prev =>
                prev.offerFilters === 'proposed' ? prev : { ...prev, offerFilters: 'proposed', page: 1, perPage: 10 }
            );
        }
    }, [isClient]);

    const [activeCard, setActiveCard] = useState(true);
    const [offers, setOffers] = useState<IOffer[]>([]);
    const [overviewCart, setOverviewCart] = useState<IOfferOverviewCart>();
    const [overviewContracts, setOverviewContracts] = useState<IContract[]>();
    const [dropdownContracts, setDropdownContracts] = useState<IContract[]>([]);
    const [infoContracts, setInfoContracts] = useState<any>();
    const [totalContractPages, setTotalContractPages] = useState(0);
    const [offerCount, setOfferCount] = useState(0);
    const [updateOffers, setUpdateOffers] = useState(false);
    const [declineReasons, setDeclineReasons] = useState<IDeclineOffer>({ declineReason: '', declineComment: '' });
    const [filterContracts, setFilterContracts] = useState({
        page: 1,
        perPage: 10,
        userIds: undefined,
    });
    const [filterDropdown] = useState({
        page: 1,
        perPage: 0,
    });
    const [totalContractCount, setTotalContractCount] = useState(0);

    useEffect(() => {
        if (
            +filterParams.priceRangeFrom &&
            +filterParams?.priceRangeTo &&
            +filterParams.priceRangeFrom > +filterParams?.priceRangeTo
        ) {
            WarningToast(t('general.toasts.invalidRange'));
            setIsOffersLoading(false);
            return;
        }
        if (!id) {
            return;
        }

        const queryKey = `${id}-${JSON.stringify({ ...filterParams, perPage: undefined })}`;
        if (lastQueryKeyRef.current !== queryKey) {
            lastQueryKeyRef.current = queryKey;
            setIsOffersLoading(true);
        }

        let isCurrent = true;
        BookingService.getBooking(+id, filterParams)
            .then(response => {
                if (!isCurrent) return;
                setOffers(response.data.offers);
                setInfoCampaign(response?.data?.info);
                setOfferCount(response.count);
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                if (isCurrent) setIsOffersLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [JSON.stringify(filterParams), updateOffers, id, hasUnreadComments]);

    useEffect(() => {
        if (!isChildCampaign) {
            return;
        }

        const queryKey = `${id}-${JSON.stringify(filterContracts)}`;
        if (lastContractsKeyRef.current !== queryKey) {
            lastContractsKeyRef.current = queryKey;
            setIsContractsLoading(true);
        }

        let isCurrent = true;
        const fetchContractOverview = async () => {
            try {
                const response = await BookingService.getContractOverview(id ? +id : undefined, filterContracts);
                if (!isCurrent) return;
                setOverviewContracts(response.data);
                setTotalContractPages(Math.ceil(response?.count / filterContracts.perPage));
                setInfoContracts(response.info);
                setTotalContractCount(response?.count);
            } catch (error) {
                if (isCurrent) ErrorToast(error);
            } finally {
                if (isCurrent) {
                    setIsContractsLoading(false);
                    setIsContractsInitialLoading(false);
                }
            }
        };
        fetchContractOverview();

        return () => {
            isCurrent = false;
        };
    }, [updateOffers, isChildCampaign, id, JSON.stringify(filterContracts)]);

    useEffect(() => {
        BookingService.getOfferCartInfo(id)
            .then(response => {
                setOverviewCart(response);
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsOverviewLoading(false));
    }, []);

    useEffect(() => {
        if (!isChildCampaign) {
            return;
        }
        const fetchContractOverview = async () => {
            try {
                const response = await BookingService.getContractOverview(id ? +id : undefined, filterContracts);
                setOverviewContracts(response.data);
                setTotalContractPages(Math.ceil(response?.count / filterParams?.perPage));
                setInfoContracts(response.info);
                setTotalContractCount(response?.count);
            } catch (error) {
                ErrorToast(error);
            }
        };
        fetchContractOverview();
    }, [updateOffers, isChildCampaign, id, JSON.stringify(filterContracts)]);

    useEffect(() => {
        if (!isChildCampaign || !id) {
            return;
        }

        const fetchContractOverview = async () => {
            try {
                const response = await BookingService.getContractOverview(+id, filterDropdown);
                setDropdownContracts(response.data);
            } catch (error) {
                ErrorToast(error);
            }
        };
        fetchContractOverview();
    }, [isChildCampaign, id, JSON.stringify(filterDropdown)]);

    function acceptOffer(offerId: number) {
        BookingService.acceptOffer(offerId)
            .then(response => {
                gtmService('brand_offer_accepted');
                SuccessToast(t('brand.campaign.book.offer.accept_offer'));
                setUpdateOffers(prevState => !prevState);
                setOverviewCart(response);
            })
            .catch(error => ErrorToast(error));
    }

    function declineOffer(offerId: number) {
        BookingService.declineOffer(offerId, declineReasons)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.decline_offer'));
                setUpdateOffers(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    function approveProposal(offerId: number) {
        BookingService.approveProposal(offerId)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.approve_proposal_success'));
                setUpdateOffers(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    function declineProposal(offerId: number) {
        BookingService.declineProposal(offerId, declineReasons)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.decline_proposal_success'));
                setUpdateOffers(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    function deleteProposal(offerId: number) {
        BookingService.deleteProposal(offerId)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.delete_proposal_success'));
                setUpdateOffers(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    function selectOffer(offerId: number, isSelected: boolean) {
        BookingService.selectOffer(offerId)
            .then(response => {
                const allOffers = [...offers];
                const selectedOfferItem = allOffers.find(item => item.id === offerId);
                if (selectedOfferItem) {
                    selectedOfferItem.isSelected = isSelected;
                    const index = selectedOfferItem ? allOffers.indexOf(selectedOfferItem) : 0;
                    allOffers[index] = selectedOfferItem;
                }
                setOffers(allOffers);
                setOverviewCart(response);
            })
            .catch(error => ErrorToast(error));
    }

    function setBestPickAction(offerId: number) {
        BookingService.setBestPick(offerId)
            .then(() => {
                setUpdateOffers(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    const clearSelectedOffers = () => {
        if (!id) {
            return;
        }
        BookingService.clearSelectedOffers(id)
            .then(response => {
                setUpdateOffers(prevState => !prevState);
                setOverviewCart(response);
            })
            .catch(error => ErrorToast(error));
    };

    const loadMoreData = () => {
        setFilterParams(prev => ({ ...prev, perPage: prev.perPage + 20 }));
    };

    const handlePages = (updatePage: number) => {
        setFilterContracts(prevState => ({ ...prevState, page: updatePage }));
        scrollToTop();
    };

    return (
        <Row>
            <Col xl={isClient ? 12 : 9}>
                <BookingFilter
                    activeCard={activeCard}
                    setActiveCard={setActiveCard}
                    info={infoCampaign}
                    offerCount={offerCount}
                    filterParams={filterParams}
                    setFilterParams={setFilterParams}
                    clearSelectedOffer={clearSelectedOffers}
                    isFilterLoading={isOffersLoading}
                />
                {isOffersLoading ? (
                    <Row>
                        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                            <BookingCardSkeleton key={i} />
                        ))}
                    </Row>
                ) : (
                    <InfiniteScroll
                        dataLength={offers?.length}
                        next={loadMoreData}
                        endMessage={
                            <p
                                className={`text-center ${
                                    offers?.length !== offerCount || filterParams?.perPage <= 10 ? 'd-none' : ''
                                }`}
                            >
                                <b>You have seen all!</b>
                            </p>
                        }
                        hasMore={offers?.length < offerCount}
                        loader={
                            <Row>
                                <BookingCardSkeleton />
                                <BookingCardSkeleton />
                            </Row>
                        }
                    >
                        <div className={!!offers?.length ? '' : 'd-none'}>
                            {activeCard ? (
                                <Row>
                                    <BookingCardItem
                                        setDeclineReason={setDeclineReasons}
                                        offers={offers}
                                        setOffers={setOffers}
                                        acceptOffer={acceptOffer}
                                        declineOffer={declineOffer}
                                        approveProposal={approveProposal}
                                        declineProposal={declineProposal}
                                        deleteProposal={deleteProposal}
                                        setBestPickAction={setBestPickAction}
                                        selectOffer={selectOffer}
                                        setUpdateOffers={setUpdateOffers}
                                    />
                                </Row>
                            ) : (
                                <BookingListItem
                                    setDeclineReason={setDeclineReasons}
                                    offers={offers}
                                    setOffers={setOffers}
                                    acceptOffer={acceptOffer}
                                    declineOffer={declineOffer}
                                    approveProposal={approveProposal}
                                    declineProposal={declineProposal}
                                    deleteProposal={deleteProposal}
                                    setBestPickAction={setBestPickAction}
                                    selectOffer={selectOffer}
                                    setUpdateOffers={setUpdateOffers}
                                />
                            )}
                        </div>
                        {!offers?.length && (
                            <EmptyState
                                desc={t(
                                    isClient
                                        ? 'brand.campaign.book.offer.empty_offers_client'
                                        : 'brand.campaign.book.offer.empty_offers'
                                )}
                                iconPath="/icons/emptyBrandCampaignBook.png"
                                btnText={isClient ? undefined : `Influencer ${t('general.button.search')}`}
                                navigateTo={isClient ? undefined : `/brand/campaign/book/${id}/invite`}
                                addClass="col-lg-5 col-xl-7 mb-4 col-12 text-center mx-auto"
                                disableButton={
                                    infoCampaign?.campaign
                                        ? infoCampaign?.campaign?.approvalStatus === 'pending' ||
                                          isDateInPast(infoCampaign?.campaign?.publishTo ?? '')
                                        : true
                                }
                                tooltipDesc={
                                    infoCampaign?.campaign?.approvalStatus === 'pending'
                                        ? t(`brand.campaign.book.invite.filters.not_approved_campaign`)
                                        : t(`brand.campaign.book.invite.filters.publication_period_passed`)
                                }
                            />
                        )}
                    </InfiniteScroll>
                )}
            </Col>
            {!isClient && (
                <Col xl={3}>
                    {isChildCampaign ? (
                        isContractsInitialLoading ? (
                            <ContractsOverviewCardSkeleton />
                        ) : (
                            <ContractsOverviewCard
                                contracts={overviewContracts}
                                info={infoContracts}
                                setUpdateList={setUpdateOffers}
                                offerFilter={filterParams.offerFilters ?? ''}
                                hasPdf={!!offers?.length}
                                handlePagination={handlePages}
                                totalPages={totalContractPages}
                                filterContracts={filterContracts}
                                contractCount={totalContractCount}
                                setFilterContracts={setFilterContracts}
                                dropdownContracts={dropdownContracts}
                                isLoading={isContractsLoading}
                            />
                        )
                    ) : isOverviewLoading ? (
                        <BookingOverviewCardSkeleton />
                    ) : (
                        <BookingOverviewCard
                            offerFilter={filterParams.offerFilters ?? ''}
                            hasSentOffers={!!infoCampaign?.sentCount}
                            overviewCartInfo={overviewCart}
                            hasPdf={!!offers?.length}
                        />
                    )}
                </Col>
            )}
        </Row>
    );
}
